import { GitSitePage } from './pages/portal/GitSite.js';
import { AccountPage } from './pages/portal/Account.js';
import { GitPage } from './pages/portal/Git.js';
import { GithubSettingsPage } from './pages/GithubSettings.js';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMe, useSystemStatus } from './api/auth.js';
import { Layout } from './components/Layout.js';
import { PortalLayout } from './components/PortalLayout.js';
import { ErrorNote, Spinner } from './components/ui.js';
import { ForcedTwoFactorEnrolPage } from './components/two-factor-gate.js';
import { ForcedPasswordChangePage } from './pages/ChangePassword.js';
import { SecurityPage } from './pages/Security.js';
import { PortalDashboardPage } from './pages/portal/Dashboard.js';
import { PortalHelpPage, PortalHelpTopicPage } from './pages/portal/Help.js';
import { PortalResourceDetailPage } from './pages/portal/ResourceDetail.js';
import { PortalSecurityPage } from './pages/portal/Security.js';
import { PortalSubscriptionDetailPage } from './pages/portal/SubscriptionDetail.js';
import { PortalSubscriptionsPage } from './pages/portal/Subscriptions.js';
import { ApplicationCreatePage } from './pages/ApplicationCreate.js';
import { ApplicationDetailPage } from './pages/ApplicationDetail.js';
import { AuditPage } from './pages/Audit.js';
import { BackupsPage } from './pages/Backups.js';
import { ClientDetailPage } from './pages/ClientDetail.js';
import { ClientsPage } from './pages/Clients.js';
import { DashboardPage } from './pages/Dashboard.js';
import { DatabaseCreatePage } from './pages/DatabaseCreate.js';
import { DatabaseDetailPage } from './pages/DatabaseDetail.js';
import { DnsPage } from './pages/Dns.js';
import { FilesPage } from './pages/Files.js';
import { GenericModulePage } from './pages/GenericModulePage.js';
import { EventsPage } from './pages/Events.js';
import { HelpPage, HelpTopicPage } from './pages/Help.js';
import { InfrastructurePage } from './pages/Infrastructure.js';
import { LoginPage } from './pages/Login.js';
import { MailPage } from './pages/Mail.js';
import { MigrationPage } from './pages/Migration.js';
import { MonitoringPage } from './pages/Monitoring.js';
import { ModulesPage } from './pages/Modules.js';
import { NotificationsPage } from './pages/Notifications.js';
import { PlansPage } from './pages/Plans.js';
import { PleskImportPage } from './pages/PleskImport.js';
import { ResellersPage } from './pages/Resellers.js';
import { ServiceCreatePage } from './pages/ServiceCreate.js';
import { ServiceDetailPage } from './pages/ServiceDetail.js';
import { ServicePoliciesPage } from './pages/ServicePolicies.js';
import { SettingsPage } from './pages/Settings.js';
import { SetupPage } from './pages/Setup.js';
import { SubscriptionDetailPage } from './pages/SubscriptionDetail.js';
import { SubscriptionsPage } from './pages/Subscriptions.js';
import { SyncPage } from './pages/Sync.js';
import { UsersPage } from './pages/Users.js';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordRecovery.js';
import { WebhooksPage } from './pages/Webhooks.js';

export function App() {
  const location = useLocation();
  const status = useSystemStatus();
  const me = useMe();

  if (status.isPending || me.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Starting Coolify Panel Manager…" />
      </div>
    );
  }

  if (status.error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="w-full max-w-md">
          <ErrorNote error={status.error} />
        </div>
      </div>
    );
  }

  if (status.data?.needsSetup) {
    return <SetupPage panelName={status.data.panelName} />;
  }

  if (location.pathname === '/forgot-password') {
    return <ForgotPasswordPage panelName={status.data?.panelName ?? 'Coolify Panel Manager'} />;
  }
  if (location.pathname === '/reset-password') {
    return <ResetPasswordPage panelName={status.data?.panelName ?? 'Coolify Panel Manager'} />;
  }

  if (!me.data) {
    return <LoginPage panelName={status.data?.panelName ?? 'Coolify Panel Manager'} />;
  }

  // Staff-issued temporary password: nothing renders until it is changed
  // (the API enforces the same rule with 403 PASSWORD_CHANGE_REQUIRED).
  if (me.data.mustChangePassword) {
    return <ForcedPasswordChangePage user={me.data} />;
  }

  // Role now requires 2FA but the user has not enrolled: forced enrolment,
  // mirroring the server's 403 TWO_FACTOR_ENROLL_REQUIRED gate (G17). Comes
  // after the password gate so a temp-password user changes it first.
  if (me.data.mustEnrollTwoFactor) {
    return <ForcedTwoFactorEnrolPage user={me.data} />;
  }

  // Tenant logins get the portal shell and ONLY the portal routes — the
  // panel routes below are never mounted for them (and the API rejects
  // every panel endpoint for the client role anyway).
  if (me.data.role === 'client' || me.data.supportClient) {
    const portalUser = me.data.supportClient
      ? { ...me.data, role: 'client' as const, clientId: me.data.supportClient.id, accountAdmin: true }
      : me.data;
    return (
      <PortalLayout
        user={portalUser}
        supportClient={me.data.supportClient}
        panelName={status.data?.panelName ?? 'Coolify Panel Manager'}
        panelVersion={status.data?.version ?? 'unknown'}
      >
        <Routes>
          <Route path="/portal" element={<PortalDashboardPage />} />
          <Route path="/portal/mail" element={<MailPage portal />} />
          <Route path="/portal/dns" element={<DnsPage portal />} />
          <Route path="/portal/subscriptions" element={<PortalSubscriptionsPage />} />
          <Route path="/portal/subscriptions/:id" element={<PortalSubscriptionDetailPage />} />
          <Route path="/portal/resources/:id" element={<PortalResourceDetailPage />} />
          <Route path="/portal/account" element={<AccountPage />} />
          {!me.data.supportClient && <Route path="/portal/git/sites/:id" element={<GitSitePage />} />}
          {!me.data.supportClient && <Route path="/portal/git" element={<GitPage />} />}
          {!me.data.supportClient && <Route path="/portal/security" element={<PortalSecurityPage />} />}
          <Route path="/portal/monitoring" element={<MonitoringPage portal />} />
          <Route path="/portal/help" element={<PortalHelpPage />} />
          <Route path="/portal/help/:moduleKey/:slug" element={<PortalHelpTopicPage />} />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </PortalLayout>
    );
  }

  return (
    <Layout
      user={me.data}
      panelName={status.data?.panelName ?? 'Coolify Panel Manager'}
      panelVersion={status.data?.version ?? 'unknown'}
    >
      <Routes>
        <Route
          path="/"
          element={<DashboardPage panelVersion={status.data?.version ?? 'unknown'} />}
        />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
        <Route path="/subscriptions/:id/applications/new" element={<ApplicationCreatePage />} />
        <Route path="/subscriptions/:id/databases/new" element={<DatabaseCreatePage />} />
        <Route path="/subscriptions/:id/services/new" element={<ServiceCreatePage />} />
        <Route path="/applications/:id" element={<ApplicationDetailPage />} />
        <Route path="/databases/:id" element={<DatabaseDetailPage />} />
        <Route path="/services/:id" element={<ServiceDetailPage />} />
        <Route path="/service-policies" element={<ServicePoliciesPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/resellers" element={<ResellersPage />} />
        <Route path="/backups" element={<BackupsPage />} />
        <Route path="/mail" element={<MailPage />} />
        <Route path="/monitoring" element={<MonitoringPage />} />
        <Route path="/dns" element={<DnsPage />} />
        <Route path="/files" element={<FilesPage />} />
        <Route path="/migration" element={<MigrationPage />} />
        <Route path="/plesk-import" element={<PleskImportPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/infrastructure" element={<InfrastructurePage />} />
        <Route path="/sync" element={<SyncPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/customer-users" element={<UsersPage customerOnly />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/modules" element={<ModulesPage />} />
        <Route path="/github/sites/:id" element={<GitSitePage provider />} />
        <Route path="/github" element={<GithubSettingsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/:moduleKey/:slug" element={<HelpTopicPage />} />
        {/* Generic server-driven page for out-of-tree modules (G21, ADR-0035).
            Static routes above win; this catches module nav paths like /m/hello. */}
        <Route path="/m/:moduleKey" element={<GenericModulePage />} />
        <Route
          path="*"
          element={
            <div className="py-24 text-center text-sm text-slate-500">Page not found.</div>
          }
        />
      </Routes>
    </Layout>
  );
}
